import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation, A11y } from "swiper/modules";
import { useTina } from "tinacms/dist/react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Static seed content — rendered as the initial data for both
// (a) production site before Tina is live, and
// (b) Tina's useTina hook (which re-hydrates it on live edits).
import homepageData from "@/content/homepage/index.json";

// Swiper core CSS
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

// The exact GraphQL query that Tina's visual editing uses to re-hydrate
// the slider state whenever the client adds / edits a slide in /admin.
const TINA_QUERY = `#graphql
  query GetHomepage($relativePath: String!) {
    homepage(relativePath: $relativePath) {
      heroSlides {
        image
        text
      }
    }
  }
`;

export default function HeroSlider() {
  // useTina returns the initial data in static/production mode, and live
  // data whenever the site is viewed inside Tina's admin iframe preview.
  const { data } = useTina({
    query: TINA_QUERY,
    variables: { relativePath: "index.json" },
    data: { homepage: homepageData },
  });

  const slides = data?.homepage?.heroSlides || [];

  return (
    <section
      data-testid="hero-slider"
      data-tina-field="homepage.heroSlides"
      aria-label="Gurukul Classes results"
      className="relative w-full overflow-hidden bg-gradient-to-b from-white via-red-50/40 to-white"
    >
      {/* Decorative blobs */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-32 top-10 h-72 w-72 rounded-full bg-red-200/40 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 bottom-10 h-72 w-72 rounded-full bg-orange-200/40 blur-3xl"
      />

      <div className="container mx-auto px-3 py-6 md:px-8 md:py-12">
        {/* Section eyebrow */}
        <div className="mb-4 flex items-center justify-center gap-3 md:mb-6">
          <span className="h-px w-8 bg-red-300" />
          <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-red-700 md:text-[11px]">
            Our Results · Year After Year
          </span>
          <span className="h-px w-8 bg-red-300" />
        </div>

        <HeroBody slides={slides} />
      </div>
    </section>
  );
}

/**
 * HeroBody — decides how to render depending on slide count:
 *   0 slides → empty-state placeholder
 *   1 slide  → static framed image (no slider overhead)
 *  ≥2 slides → auto-initialised Swiper carousel
 */
function HeroBody({ slides }) {
  if (!slides || slides.length === 0) {
    return (
      <div className="mx-auto max-w-3xl">
        <SlideFrame empty />
      </div>
    );
  }

  if (slides.length === 1) {
    return (
      <div className="mx-auto max-w-5xl">
        <SlideFrame src={slides[0].image} label={slides[0].text} eager />
      </div>
    );
  }

  return <HeroCarousel slides={slides} />;
}

function HeroCarousel({ slides }) {
  // Give each Swiper instance a unique id so custom nav buttons bind correctly.
  const [navId] = useState(
    () => `hero-nav-${Math.random().toString(36).slice(2, 9)}`
  );

  // Re-init Swiper when the number of slides changes (e.g. client adds one
  // via Tina admin) — Swiper's `key` trick forces a clean remount.
  return (
    <div className="relative">
      <Swiper
        key={slides.length}
        modules={[Autoplay, Pagination, Navigation, A11y]}
        slidesPerView={1}
        loop={slides.length > 1}
        autoplay={{ delay: 4000, disableOnInteraction: false }}
        pagination={{
          clickable: true,
          bulletClass:
            "swiper-pagination-bullet !bg-slate-300 !opacity-100 !w-2 !h-2",
          bulletActiveClass:
            "swiper-pagination-bullet-active !bg-red-600 !w-6",
        }}
        navigation={{
          prevEl: `.${navId}-prev`,
          nextEl: `.${navId}-next`,
        }}
        className="rounded-md"
      >
        {slides.map((s, i) => (
          <SwiperSlide key={`${s.image}-${i}`} data-testid={`hero-slide-${i}`}>
            <SlideFrame src={s.image} label={s.text} eager={i === 0} />
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Custom prev / next (desktop only, mobile uses swipe) */}
      <button
        type="button"
        aria-label="Previous slide"
        data-testid="hero-prev"
        className={`${navId}-prev absolute left-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-700 shadow-md backdrop-blur transition-all hover:bg-red-600 hover:text-white md:flex`}
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        aria-label="Next slide"
        data-testid="hero-next"
        className={`${navId}-next absolute right-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-700 shadow-md backdrop-blur transition-all hover:bg-red-600 hover:text-white md:flex`}
      >
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
}

/**
 * SlideFrame — identical framed canvas for every image so different
 * aspect ratios all render at the same dimensions.
 */
function SlideFrame({ src, label, eager, empty }) {
  return (
    <div className="relative w-full">
      <div className="relative aspect-[4/5] w-full overflow-hidden rounded-md border border-red-100 bg-gradient-to-br from-red-50 via-orange-50 to-white sm:aspect-[5/4] md:aspect-[16/9]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.6),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(255,200,170,0.35),transparent_45%)]"
        />
        <span className="absolute left-3 top-3 h-6 w-6 rounded-tl-md border-l-2 border-t-2 border-red-500/70 md:left-5 md:top-5 md:h-8 md:w-8" />
        <span className="absolute right-3 top-3 h-6 w-6 rounded-tr-md border-r-2 border-t-2 border-red-500/70 md:right-5 md:top-5 md:h-8 md:w-8" />
        <span className="absolute bottom-3 left-3 h-6 w-6 rounded-bl-md border-b-2 border-l-2 border-red-500/70 md:bottom-5 md:left-5 md:h-8 md:w-8" />
        <span className="absolute bottom-3 right-3 h-6 w-6 rounded-br-md border-b-2 border-r-2 border-red-500/70 md:bottom-5 md:right-5 md:h-8 md:w-8" />

        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4 md:p-8">
          {src ? (
            <img
              src={src}
              alt={label || ""}
              loading={eager ? "eager" : "lazy"}
              className="max-h-full max-w-full rounded-sm object-contain shadow-xl shadow-red-900/15"
              draggable={false}
            />
          ) : (
            <div className="flex flex-col items-center gap-2 text-slate-500">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-red-700">
                Add your first slide
              </span>
              <span className="max-w-[80%] text-center text-[12px] text-slate-500">
                Open /admin → Homepage → Hero Slides to upload images.
              </span>
            </div>
          )}
        </div>

        {/* Caption overlay — only if slide has a text */}
        {src && label && (
          <div className="pointer-events-none absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-4 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-white backdrop-blur md:text-xs">
            {label}
          </div>
        )}
      </div>
    </div>
  );
}
